/**
 * Quantity-mode (MDOT-24700) composition builder: a decorator hook around
 * {@link useInstrumentEditorBuilder} that layers the fixed-AUM quantity/market-value/weight
 * edit model on top of the shared weight-based editor, using only its public primitives.
 *
 * Edit model: AUM is a FIXED budget (see `./derive`). Every edit preserves
 * `AUM = Σ rowInvestedMarketValue + cashMarketValue` (I1, see `./derive`) by rebalancing the structural
 * cash row, which may go negative — submit is blocked while it is (I4, enforced by the
 * consumer). When no `quantity` state is provided at init/reset, the wrapper degrades to
 * plain weight behavior (quantity methods no-op).
 *
 * The market date rides here too (MDOT-25190), in both modes (MDOT-25192): it lives in the page's
 * query string, so this hook needs a router in context wherever it is rendered. In quantity mode
 * changing it re-prices the composition (MDOT-25191), which is why {@link getMarketDate}'s `set` is
 * composed here rather than handed out raw: the confirmation an edited composition owes, the price
 * call and the URL write are one function, and every caller — the picker's Apply, the footer's
 * Return to today, the load-time path — goes through it. In weight mode all three degrade to the
 * URL write, which is the whole of what the day does there.
 *
 * Import rule: this module may import only `../builder`, `../const` and its own siblings —
 * never `../instrumentEditor.types` (that file imports from here to build its union).
 */
import { reportPlatformError } from "$root/api/error-reporting/report";
import { platformToast } from "$root/notification-system/toast";
import { makeTaskQueue, serverTodayAsLocalDate } from "$root/utils";
import { parallelizeWithAllSettled } from "$root/utils/promise";
import type { Currencies, EditorVerifyExcelRequestPolicyEnum, ResolvedInstrument } from "@eu.mdotm.api/sphere";
import { type MaybeFn, noop } from "@mdotm/shadcn/legacy/utils";
import i18n from "i18next";
import { Set } from "immutable";
import { useMemo, useRef, useState } from "react";
import type { InstrumentEditor, InstrumentEditorBuilderProps } from "../builder";
import { useInstrumentEditorBuilder } from "../builder";
import type { Instrument } from "../const";
import { asNewPosition } from "./previous-position";
import type { QuantityEditResult } from "./derive";
import { isHeldBlocked } from "./held-issues/heldIssue";
import { isHeldByValueByNature } from "./heldByValue";
import { applyAumEdit, computeCashMarketValue, deriveWeight } from "./derive";
import type { PricingByIdentifier } from "./market-date/pricing";
import {
	applyPricing,
	hasUnpricedRows,
	planPricing,
	pricingIdentifiers,
	rememberLoadedPricing,
} from "./market-date/pricing";
import { fetchPricing } from "./market-date/reprice";
import { spawnRepriceConfirmDialog } from "./market-date/RepriceConfirmDialog";
import type { MarketDateControl } from "./market-date/useMarketDate";
import { useMarketDate } from "./market-date/useMarketDate";
import type { NewInstrumentFix } from "./new-instruments/checks";
import {
	applyCheckAnswers,
	blocksSave,
	checkRequests,
	failedRows,
	isCheckable,
	isLocked,
	restartCheck,
	rowsToRecheck,
	startCheck,
	checkRequest,
	withNewIdentifier,
	withRiskModel,
} from "./new-instruments/checks";
import { resolveInstruments } from "./new-instruments/resolve";
import { parseSizingAnswer } from "./position-sizing/answer";
import { positionSizingRepository } from "./position-sizing/repository";
import type { SizingEdit } from "./position-sizing/sizing";
import {
	applySizingAnswer,
	canSize,
	failSizing,
	hasPendingSizing,
	isCurrentSizing,
	MissingSizingCurrencyError,
	sanitiseEdit,
	settledValuations,
	sizingRequest,
	startSizing,
	valuationsAt,
} from "./position-sizing/sizing";
import { rememberLoadedValue } from "./position-sizing/valueByDate";
import { sizingMessageOf, withSizingMessage } from "./sizing-message/sizingMessage";
import { toastSizingMessage } from "./sizing-message/toastSizingMessage";
import {
	planQuantityUpload,
	uploadedSizings,
	withoutUploadedQuantity,
	withUploadedQuantity,
} from "./upload/uploadedQuantity";

/**
 * Quantity-mode editor state: present only when the composition is edited as quantities
 * against a fixed AUM budget (cash rebalances on every edit, see `./derive`).
 */
export type QuantityModeState = {
	/** The fixed AUM budget every edit must preserve (I1). */
	aum: number;
	/** Currency of the AUM budget: the currency symbol on monetary amounts, and what every sizing is asked in (`./position-sizing`). */
	currency?: Currencies;
	/**
	 * The portfolio the price, check and sizing calls are addressed to (MDOT-25191). Absent while the
	 * composition belongs to no portfolio yet (a creation in quantities): the calls then go to their
	 * portfolio-less twins, asked in {@link currency}.
	 */
	investmentUuid?: string;
};

type InstrumentBuilderInit = Parameters<typeof useInstrumentEditorBuilder>[0];

export type QuantityEditorBuilderInit = InstrumentBuilderInit & {
	/** Enables quantity mode; omit (or reset without it) for plain weight behavior. */
	quantity?: QuantityModeState;
	/**
	 * The portfolio's last trade date (a UTC timestamp or `YYYY-MM-DD`): the earliest day the
	 * composition can be dated to, and so the lower bound of the market date's window. It sits
	 * outside `quantity` because the date control is offered in both modes (MDOT-25192), so both
	 * reset paths supply it.
	 */
	lastTradeDate?: string;
};

export type QuantityEditorBuilderProps = Omit<InstrumentEditorBuilderProps, "reset"> & {
	/** No-op unless the builder was init/reset with `quantity`. */
	setQuantity(ticker: string, rawQuantity: number): void;
	/** No-op unless the builder was init/reset with `quantity`. */
	setMarketValue(ticker: string, rawMarketValue: number): void;
	/**
	 * Changes the fixed AUM budget itself — the "raise the portfolio AUM" recovery path out
	 * of negative cash (I4): every row's weight re-derives against the new budget and cash
	 * absorbs the difference. No-op outside quantity mode or when `rawAum` is not a positive
	 * finite number.
	 */
	setAum(rawAum: number): void;
	/** Derived `AUM − Σ rowInvestedMarketValue` over the invested rows; `null` outside quantity mode. */
	getCashMarketValue(): number | null;
	/** True while the composition overspends the fixed AUM (I4) — submit must be blocked. */
	hasNegativeCash(): boolean;
	getAum(): number | null;
	/** Currency of the AUM budget; `null` outside quantity mode or when unknown. */
	getCurrency(): Currencies | null;
	/**
	 * The day the composition is edited against, and the one way to change it (MDOT-25190). In
	 * quantity mode it is the day the market prices are taken at; in weight mode it is the day the
	 * save is dated to and nothing more (MDOT-25192). Always present — every composition has a day,
	 * and the control greys itself where that day is the only one on offer.
	 */
	getMarketDate(): MarketDateControl;
	/**
	 * Prices the composition at `date` and writes the result back, without touching the URL or
	 * asking anything (MDOT-25191). The load path for a deep-linked `?marketDate`, where the day is
	 * already settled and the user has entered nothing to confirm about. Resolves once the grid has
	 * the new values; a no-op outside quantity mode.
	 */
	repriceAt(date: string): Promise<void>;
	/** True while a row carries no price at the market date — submit must be blocked. */
	hasUnpricedRows(): boolean;
	/** True while an added instrument is being checked or waits on a fix — submit must be blocked. */
	hasRowsNeedingFix(): boolean;
	/** True while a typed quantity, market value or weight waits on Cold War. */
	hasPendingSizing(): boolean;
	/**
	 * Resolves once every typed quantity, market value or weight sent to Cold War so far has its answer
	 * written into the composition. A save awaits it before it reads the composition.
	 */
	settleSizing(): Promise<void>;
	/**
	 * Applies what the user did about an added instrument's issue: a new identifier checks the row
	 * again, a risk model clears it and prices it, a retry repeats a check that did not answer.
	 */
	fixNewInstrument(id: string, fix: NewInstrumentFix): void;
	/** Checks again, in one call, every added instrument whose check got no answer. */
	retryFailedChecks(): void;
	/** Values again on Cold War, at the market date, every row whose valuation got no answer. */
	retryValuations(): void;
	/**
	 * Takes in the rows of an uploaded composition file by their quantity, else their weight
	 * (`./upload`): a row the composition holds is sized again when `policy` gives the file priority,
	 * a new one is added and sized once it can be. No-op outside quantity mode.
	 */
	uploadQuantities(rows: Instrument[], policy: EditorVerifyExcelRequestPolicyEnum): void;
	isQuantityMode(): boolean;
	reset(
		resetDataOrFn: MaybeFn<QuantityEditorBuilderInit, [QuantityEditorBuilderInit]>,
		opt?: { disableToggleDirty?: boolean },
	): void;
};

/** How many rows a reprice values on Cold War at once (`valueRows`). */
const VALUATION_CONCURRENCY = 3;

/**
 * The init as quantity mode needs it: a composition that already knows today's prices, and the
 * structural cash row inside it.
 *
 * The rows arrive priced at their latest close — today's answer, by the pricing engine's own rule —
 * so the day the editor opens on is remembered as answered before anything is asked (MDOT-25191).
 * The market date the editor then opens at does not enter into it: a deep-linked past day is priced
 * by the load path's own call, and what the composition arrived carrying is today's either way.
 */
function normalizeQuantityInit(init: QuantityEditorBuilderInit): QuantityEditorBuilderInit {
	if (!init.quantity) {
		return init;
	}
	const today = serverTodayAsLocalDate();
	const { aum } = init.quantity;
	return ensureQuantityCashRow({
		...init,
		composition: init.composition.map((row) => rememberLoadedValue(rememberLoadedPricing(row, today), today, aum)),
	});
}

/**
 * Quantity-mode reset path: the cash row must live INSIDE the composition map
 * (`isCash: true`) — not only as the `cash` singleton — so the grid renders and rebalances
 * it. When the init enables quantity mode without providing one, inject it (reusing the
 * cash singleton's identity when present) with its weight derived from the AUM remainder.
 */
function ensureQuantityCashRow(init: QuantityEditorBuilderInit): QuantityEditorBuilderInit {
	if (!init.quantity || init.composition.some((instrument) => Boolean(instrument.isCash))) {
		return init;
	}

	const cashMarketValue = computeCashMarketValue(init.composition.values(), init.quantity.aum);
	const cashRow: Instrument = {
		id: "CASH",
		instrumentType: "INSTRUMENT",
		name: "Cash",
		...init.cash,
		isCash: true,
		deleted: false,
		quantity: undefined,
		price: undefined,
		marketValue: cashMarketValue,
		weight: deriveWeight(cashMarketValue, init.quantity.aum),
	};
	return { ...init, composition: init.composition.set(cashRow.id, cashRow) };
}

/**
 * Decorator hook over {@link useInstrumentEditorBuilder}. The returned object is a
 * structural supertype of {@link InstrumentEditorBuilderProps}, so it passes anywhere the
 * base builder is expected. Quantity state (`aum`) is owned here, not by the shared
 * {@link InstrumentEditor} class.
 *
 * Staleness-safe by construction: every method routes through the base hook's primitives,
 * which read the latest builder from its chained ref — so captured references to this
 * object (e.g. in AG Grid column defs) always act on current state.
 */
export function useQuantityEditorBuilder(initializer: QuantityEditorBuilderInit): QuantityEditorBuilderProps {
	const [initialInit] = useState(() => normalizeQuantityInit(initializer));
	const base = useInstrumentEditorBuilder(initialInit);
	// Wrapper-owned quantity state. A ref (not React state) because it only ever changes
	// inside `reset`, which already re-renders through the base hook's setState — and the
	// ref keeps same-tick reads correct for multi-step operations.
	const quantityRef = useRef(initialInit.quantity);
	// The window's lower bound is the one piece of quantity state the render path reads, so it is
	// mirrored in React state: a ref would leave the date control bounded by whatever the previous
	// render saw. `reset` writes both.
	const [lastTradeDate, setLastTradeDate] = useState(initialInit.lastTradeDate);
	const marketDate = useMarketDate({ lastTradeDate });
	// True while a price call is out — the date control greys, so two days can never be in flight at
	// once.
	const [repricing, setRepricing] = useState(false);
	// Numbers each new-instrument check, so an answer that lands after the row was checked again is
	// dropped (see `./new-instruments/checks`).
	const checkAttemptRef = useRef(0);
	// Numbers each sizing on Cold War, so an answer about a sizing the row has moved on from is dropped
	// (see `./position-sizing/sizing`).
	const sizingAttemptRef = useRef(0);
	// Sizings go to Cold War one at a time, in the order they were typed: each answer rebalances cash
	// against the one before it.
	const [sizingQueue] = useState(() => makeTaskQueue());

	return useMemo(() => {
		/**
		 * Rows as the derivation engine sees them: soft deletion is tracked both on the row
		 * flag and in the deleted set, so normalize the latter onto the flag before handing
		 * over.
		 */
		const engineRows = (): Instrument[] => {
			const deleted = base.latestDeleted();
			return Array.from(base.latestComposition().values()).map((instrument) =>
				!instrument.deleted && deleted.has(instrument.id) ? { ...instrument, deleted: true } : instrument,
			);
		};

		/**
		 * Writes back only the edited row and the cash row — the engine never changes
		 * anything else (fixed AUM ⇒ other rows' weights are untouched). Restores each row's
		 * original `deleted` flag, which {@link engineRows} may have forced for the engine's
		 * benefit.
		 */
		const applyEditResult = (editedId: string, result: QuantityEditResult): InstrumentEditor | undefined => {
			const composition = base.latestComposition();
			let editor: InstrumentEditor | undefined;
			for (const row of result.rows) {
				if (row.id === editedId || row.isCash) {
					editor = base.setInstrument(row.id, { ...row, deleted: composition.get(row.id)?.deleted });
				}
			}
			return editor;
		};

		/**
		 * Re-derives the cash row's weight after a structural change (delete/restore),
		 * routing the freed (or re-claimed) market value to cash. No-op in weight mode.
		 */
		const recomputeCashWeight = (): InstrumentEditor | undefined => {
			const quantity = quantityRef.current;
			if (!quantity) {
				return undefined;
			}
			const cashRow = base.latestComposition().find((instrument) => Boolean(instrument.isCash));
			if (!cashRow) {
				return undefined;
			}
			const cashMarketValue = computeCashMarketValue(engineRows(), quantity.aum);
			return base.setInstrument(cashRow.id, {
				...cashRow,
				marketValue: cashMarketValue,
				weight: deriveWeight(cashMarketValue, quantity.aum),
			});
		};

		const isCashRow = (id: string): boolean => {
			return Boolean(base.latestComposition().get(id)?.isCash);
		};

		const getCashMarketValue = (): number | null => {
			const quantity = quantityRef.current;
			if (!quantity) {
				return null;
			}
			return computeCashMarketValue(engineRows(), quantity.aum);
		};

		/**
		 * Writes a pricing answer into the grid (MDOT-25191). The write goes through `reset` with
		 * `disableToggleDirty`, which can only ever SET the dirty flag — so a price landing never marks
		 * the composition dirty (the user typed nothing), and an edit made before it survives as dirty.
		 *
		 * The answer is applied to the grid as it stands when it arrives, not as it stood when it was
		 * asked for, and a row the answer does not mention is passed through untouched — which is what
		 * lets an add ask about its own rows alone and leave the rest of the composition where it is.
		 *
		 * Row identity and soft-deletion are preserved: the engine sees the deleted flag normalised
		 * onto the rows, and the write restores whatever each row carried, leaving the deleted set the
		 * single source of truth it already is.
		 *
		 * A share row the answer leaves without a value at the day for its shares is then valued on Cold
		 * War (`valueRows`); the promise, when there is one, resolves once those values are written.
		 */
		const applyPricingAnswer = (
			pricing: PricingByIdentifier,
			date: string,
			rows = engineRows(),
		): Promise<void> | undefined => {
			const quantity = quantityRef.current;
			if (!quantity) {
				return undefined;
			}
			writeRows(applyPricing({ rows, pricing, aum: quantity.aum, date }).rows);
			sizeUploadedRows();
			return valueRows(date);
		};

		/** Writes rows back without marking the composition dirty, each keeping the `deleted` flag it has. */
		const writeRows = (rows: Instrument[]): void => {
			base.reset(
				(current) => ({
					...current,
					composition: rows.reduce(
						(composition, row) => composition.set(row.id, { ...row, deleted: composition.get(row.id)?.deleted }),
						current.composition,
					),
				}),
				{ disableToggleDirty: true },
			);
		};

		/**
		 * Asks the pricing endpoint about `rows` at `date`, writes what comes back and values the share
		 * rows at the day. `repricing` is raised around the calls, so the date control greys and a date
		 * change can never race a price or a value already in flight.
		 *
		 * Rows with nothing to ask about make no call, so they must not grey the control for a tick
		 * either: the flag goes up only when a request is actually going out. A day every row was already
		 * priced and valued at is that same case — its answers come off the rows, and the grid is
		 * re-projected from them without a request. Past that, `fetchPricing` always answers about every identifier it
		 * asked about, failures included.
		 */
		const priceRows = async (rows: Instrument[], date: string): Promise<void> => {
			const quantity = quantityRef.current;
			if (!quantity || pricingIdentifiers(rows).length === 0) {
				return;
			}
			const plan = planPricing(rows, date);
			if (plan.missing.length === 0) {
				const valuing = applyPricingAnswer(plan.known, date);
				if (!valuing) {
					return;
				}
				setRepricing(true);
				try {
					await valuing;
				} finally {
					setRepricing(false);
				}
				return;
			}
			setRepricing(true);
			try {
				const { investmentUuid, currency, aum } = quantity;
				await applyPricingAnswer(await fetchPricing({ investmentUuid, currency, aum, date, plan }), date);
			} finally {
				setRepricing(false);
			}
		};

		/**
		 * Runs the engine's check on `rows`, which are already in the composition waiting on it, and
		 * writes what comes back: a clear row is priced from the answer in the same write, a row with an
		 * issue stays locked. A failed call fails the rows — they block the save until retried.
		 */
		const runChecks = async (rows: Instrument[], date: string): Promise<void> => {
			const quantity = quantityRef.current;
			const attempt = rows[0]?.newInstrumentCheck?.attempt;
			if (!quantity || attempt == null) {
				return;
			}
			let answers: ResolvedInstrument[] | undefined;
			try {
				answers = await resolveInstruments({
					investmentUuid: quantity.investmentUuid,
					instruments: checkRequests(rows),
					referenceDate: date,
					currency: quantity.currency,
				});
			} catch {
				answers = undefined;
			}
			const checked = applyCheckAnswers(engineRows(), answers, attempt);
			void applyPricingAnswer(checked.pricing, date, checked.rows);
		};

		/**
		 * Whether an added row goes through the check: an instrument, in a quantity composition. A row
		 * held by value (an index, a live proxy) is not — the check is what prices a row, and it has no
		 * price to take.
		 */
		const needsCheck = (row: Instrument): boolean => {
			return quantityRef.current != null && isCheckable(row) && !row.heldByValue;
		};

		/**
		 * The rows as they enter the composition: each one {@link needsCheck} names waits on one check,
		 * asked at `date`; the rest pass through to be priced straight away.
		 */
		const startChecks = (rows: Instrument[], date: string): Instrument[] => {
			checkAttemptRef.current += 1;
			const attempt = checkAttemptRef.current;
			return rows.map((row) =>
				needsCheck(row) ? startCheck(row, { request: checkRequest(row), date, attempt }) : row,
			);
		};

		/** Sends each added row down its path: the check for an instrument, the price call for the rest. */
		const checkOrPrice = (added: Instrument[], date: string): void => {
			const checking = added.filter((row) => row.newInstrumentCheck != null);
			if (checking.length > 0) {
				void runChecks(checking, date);
			}
			// A row held by value by its nature has no price to wait for: it is sized in money as it lands.
			void priceRows(
				added.filter((row) => row.newInstrumentCheck == null && !row.heldByValue),
				date,
			);
		};

		/**
		 * Prices every row in the composition at `date` (MDOT-25191). A row an added-instrument check
		 * has locked has no price by construction and is left alone; one still owed an answer or a fix
		 * is checked again at the new day. A row held by value (MDOT-25244) is left alone too: its value
		 * does not depend on the day's prices, so a date change keeps the market value it carries. So
		 * is a held row blocked by its issue (`./held-issues`): nothing about its position can change.
		 */
		const repriceAt = (date: string): Promise<void> => {
			const rows = engineRows();
			const recheck = rowsToRecheck(rows, date);
			if (recheck.length > 0) {
				checkAttemptRef.current += 1;
				const attempt = checkAttemptRef.current;
				const restarted = recheck.map((row) => restartCheck(row, { date, attempt }));
				writeRows(restarted);
				void runChecks(restarted, date);
			}
			return priceRows(
				rows.filter((row) => !isLocked(row) && !row.heldByValue && !isHeldBlocked(row)),
				date,
			);
		};

		/**
		 * A row as it enters a quantity composition (MDOT-25245): quantity, price, market value and
		 * weight all blank — nothing is invested, so cash is untouched (I1) until the user sizes it. Whatever
		 * weight the shared adder dialog gave it is dropped — here a weight is a spend instruction, and
		 * the user asked for a row, not for a position. A nested portfolio, an index or a live proxy
		 * is held by value from the start (`./heldByValue`): no share count, so its market value and
		 * weight are editable as soon as it lands.
		 */
		const emptyQuantityRow = (row: Instrument): Instrument => {
			const heldByValue = isHeldByValueByNature(row);
			return asNewPosition({
				...row,
				heldByValue,
				quantity: undefined,
				price: undefined,
				marketValue: undefined,
				weight: undefined,
			});
		};

		/**
		 * Sends the sizing row `id` waits on (`attempt`) to Cold War and writes the answer back, or fails
		 * the row when no answer comes. A sizing the row has moved on from by the time its turn comes is
		 * never sent. A `typed` sizing is the user's edit: its answer marks the composition dirty, and
		 * toasts what Cold War said about it when `announce` is set. A valuation (`valueRows`) does neither.
		 */
		const sendSizing = async (params: {
			id: string;
			edit: SizingEdit;
			attempt: number;
			date: string;
			signal: AbortSignal;
			typed: boolean;
			announce: boolean;
		}): Promise<void> => {
			const { id, edit, attempt, date, signal, typed, announce } = params;
			const rowBeingSized = base.latestComposition().get(id);
			const current = quantityRef.current;
			if (!isCurrentSizing(rowBeingSized, attempt) || !current) {
				return;
			}
			try {
				const request = sizingRequest(rowBeingSized, edit, { aum: current.aum, currency: current.currency, date });
				const answer = parseSizingAnswer(
					await positionSizingRepository.deriveInstrumentValue(current.investmentUuid, request, signal),
				);
				const rows = engineRows();
				const answeredRow = rows.find((row) => row.id === id);
				const result = applySizingAnswer({
					rows,
					id,
					attempt,
					answer,
					aum: quantityRef.current?.aum ?? current.aum,
					date,
				});
				if (!result || !answeredRow) {
					return;
				}
				const sizingMessage = sizingMessageOf(answer, edit, {
					currency: current.currency,
					heldByValue: result.rows.find((row) => row.id === id)?.heldByValue === true,
					receivedAt: new Date().toISOString(),
					toasted: typed && announce,
				});
				const settled = withSizingMessage(result, id, sizingMessage);
				if (!typed) {
					writeRows(settled.rows.filter((row) => row.id === id || row.isCash));
					return;
				}
				applyEditResult(id, settled);
				if (sizingMessage && announce) {
					toastSizingMessage(answeredRow, sizingMessage);
				}
			} catch (error) {
				if (error instanceof MissingSizingCurrencyError) {
					reportPlatformError(error, "ERROR", "portfolio", {
						message: `sizing ${id} of investment ${current.investmentUuid ?? "(none yet)"}`,
					});
					platformToast({
						children: i18n.t("EDIT_COMPOSITION.POSITION_SIZING.MISSING_CURRENCY"),
						severity: "error",
						icon: "Portfolio",
					});
				}
				const failed = failSizing(base.latestComposition().get(id), {
					attempt,
					now: new Date().toISOString(),
					message: error instanceof Error ? error.message : String(error),
				});
				if (failed) {
					writeRows([failed]);
				}
			}
		};

		/**
		 * Queues what the user typed on row `id` for Cold War (MDOT-25670) and writes the answer back. The
		 * row waits from the moment the edit lands until the answer does, and its values stay as they
		 * were until then: cash moves only on Cold War's numbers. Returns the editor with the row
		 * waiting, or `undefined` when the edit is not one that can be sized. A row sized from an upload
		 * toasts nothing: the editor's banner sums up every row Cold War resized (`./sizing-message`).
		 */
		const sizeRow = (
			id: string,
			typed: SizingEdit,
			source: "edit" | "upload" = "edit",
		): InstrumentEditor | undefined => {
			const quantity = quantityRef.current;
			const row = base.latestComposition().get(id);
			const edit = sanitiseEdit(typed);
			if (!quantity || !row || !edit || !canSize(row, edit.field)) {
				return undefined;
			}
			sizingAttemptRef.current += 1;
			const attempt = sizingAttemptRef.current;
			const date = marketDate.value;
			const editor = base.setInstrument(id, startSizing(row, { edit, attempt, now: new Date().toISOString() }));
			void sizingQueue.enqueue((signal) =>
				sendSizing({ id, edit, attempt, date, signal, typed: true, announce: source === "edit" }),
			);
			return editor;
		};

		/**
		 * Values every share row priced at `date` that has no value there for the shares it holds and the
		 * AUM (`./position-sizing/valueByDate`): Cold War sizes each by its own shares at the day,
		 * {@link VALUATION_CONCURRENCY} at a time, in one turn of the sizing queue, so no typed sizing
		 * lands in the middle of it. The rows wait as a typed sizing does, without marking the
		 * composition dirty. A row whose valuation failed is asked again, unless the day already has its
		 * value, which clears the failure. `undefined` when no row needs a call; otherwise resolves once
		 * every answer is written.
		 */
		const valueRows = (date: string): Promise<void> | undefined => {
			const quantity = quantityRef.current;
			if (!quantity) {
				return undefined;
			}
			const rows = engineRows();
			const settled = settledValuations(rows, date, quantity.aum);
			if (settled.length > 0) {
				writeRows(settled);
			}
			const valuations = valuationsAt(rows, date, quantity.aum);
			if (valuations.length === 0) {
				return undefined;
			}
			const now = new Date().toISOString();
			const started = valuations.map(({ id, edit }) => {
				sizingAttemptRef.current += 1;
				return { id, edit, attempt: sizingAttemptRef.current };
			});
			const byId = new Map(rows.map((row) => [row.id, row]));
			writeRows(
				started.flatMap(({ id, edit, attempt }) => {
					const row = byId.get(id);
					return row ? [startSizing(row, { edit, attempt, now, valuationDate: date })] : [];
				}),
			);
			return sizingQueue
				.enqueue(async (signal) => {
					await parallelizeWithAllSettled(
						started.map(
							({ id, edit, attempt }) =>
								() =>
									sendSizing({ id, edit, attempt, date, signal, typed: false, announce: false }),
						),
						{ concurrency: VALUATION_CONCURRENCY },
					);
				})
				.catch(noop);
		};

		/**
		 * Sizes every uploaded row that can be sized now by the file's value, once (`./upload`).
		 */
		const sizeUploadedRows = (): void => {
			for (const { id, edit } of uploadedSizings(engineRows())) {
				const row = base.latestComposition().get(id);
				if (row) {
					base.setInstrument(id, withoutUploadedQuantity(row));
					sizeRow(id, edit, "upload");
				}
			}
		};

		/**
		 * The one way the market date changes. An edited QUANTITY composition is asked first (AC-6),
		 * because repricing rewrites what its rows are worth; a weight composition is asked nothing,
		 * edited or not, since its rows carry no price and the day is only what the save is dated to
		 * (MDOT-25192). The URL is written once the prices have landed, so a cancel or a failure leaves
		 * the day, the link and the grid exactly where they were.
		 */
		const setMarketDate = async (date: string): Promise<void> => {
			// A row being sized was asked about at the day on screen, so its answer lands before the day moves.
			await sizingQueue.drain();
			if (quantityRef.current && base.isDirty && !(await spawnRepriceConfirmDialog({ date }).promise)) {
				return;
			}
			await repriceAt(date);
			marketDate.set(date);
		};

		const marketDateControl: MarketDateControl = {
			...marketDate,
			repricing,
			set: (date) => void setMarketDate(date),
		};

		return {
			...base,
			isQuantityMode: () => quantityRef.current != null,
			getAum: () => quantityRef.current?.aum ?? null,
			getCurrency: () => quantityRef.current?.currency ?? null,
			getMarketDate: () => marketDateControl,
			repriceAt,
			hasUnpricedRows: () => hasUnpricedRows(engineRows()),
			hasRowsNeedingFix: () => blocksSave(engineRows()),
			hasPendingSizing: () => hasPendingSizing(engineRows()),
			settleSizing: () => sizingQueue.drain(),
			fixNewInstrument(id, fix) {
				const row = base.latestComposition().get(id);
				if (!row?.newInstrumentCheck) {
					return;
				}
				const date = marketDate.value;
				if (fix.kind === "riskModel") {
					const fixed = withRiskModel(row, fix.proxies);
					base.setInstrument(id, fixed);
					void priceRows([fixed], date);
					return;
				}
				checkAttemptRef.current += 1;
				const attempt = checkAttemptRef.current;
				const next =
					fix.kind === "identifier"
						? withNewIdentifier(row, { identifier: fix.identifier, date, attempt })
						: restartCheck(row, { date, attempt });
				base.setInstrument(id, next);
				void runChecks([next], date);
			},
			retryFailedChecks() {
				const failed = failedRows(engineRows());
				if (failed.length === 0) {
					return;
				}
				checkAttemptRef.current += 1;
				const attempt = checkAttemptRef.current;
				const date = marketDate.value;
				const restarted = failed.map((row) => restartCheck(row, { date, attempt }));
				writeRows(restarted);
				void runChecks(restarted, date);
			},
			retryValuations() {
				void valueRows(marketDate.value);
			},
			uploadQuantities(uploaded, policy) {
				if (quantityRef.current == null) {
					return;
				}
				const { added, resized } = planQuantityUpload(uploaded, base.latestComposition(), policy);
				const date = marketDate.value;
				const rows = startChecks(
					added.map(({ row, edit }) => withUploadedQuantity(emptyQuantityRow(row), edit)),
					date,
				);
				base.bulkAddInstruments(rows);
				checkOrPrice(rows, date);
				// A row held by value by its nature waits on no price: its weight is sized as it lands.
				sizeUploadedRows();
				for (const { id, edit } of resized) {
					sizeRow(id, edit, "upload");
				}
			},
			getCashMarketValue,
			hasNegativeCash: () => (getCashMarketValue() ?? 0) < 0,
			setQuantity(ticker, rawQuantity) {
				const quantity = quantityRef.current;
				if (!quantity) {
					return;
				}
				sizeRow(ticker, { field: "QUANTITY", value: rawQuantity });
			},
			setMarketValue(ticker, rawMarketValue) {
				const quantity = quantityRef.current;
				if (!quantity) {
					return;
				}
				sizeRow(ticker, { field: "MARKET_VALUE", value: rawMarketValue });
			},
			// In quantity mode a typed weight is a spend instruction, not a raw field write: Cold War sizes
			// the row from it like a typed quantity or market value. Overriding here also shields generic
			// setWeight callers from writing weights that would break the quantity invariants. Outside
			// quantity mode, base behavior.
			setWeight(ticker, newWeight) {
				if (!quantityRef.current) {
					return base.setWeight(ticker, newWeight);
				}
				const row = base.latestComposition().get(ticker);
				return (
					sizeRow(ticker, { field: "WEIGHT", value: newWeight ?? 0 }) ??
					// An edit that cannot be sized leaves the row as it is.
					(row ? base.setInstrument(ticker, row) : base.setWeight(ticker, newWeight))
				);
			},
			// A row added to a quantity composition is an empty line until it has a price at the market
			// date (MDOT-25245), and the shared adder dialogs — which serve every other editor too —
			// know nothing about prices. So the pricing rides here, over the rows they produce: the row
			// lands first and synchronously, keeping the base method's signature, and the answer arrives
			// after and decides what the row is — priced with an editable quantity, held by its market
			// value, or unpriced. Until it does the row carries no unit price, so `./derive` leaves a
			// quantity typed at it alone (the value it would derive is `quantity × 0`). An instrument is
			// checked by the engine first (`./new-instruments`) and priced from that answer, and stays
			// locked while the check is out or has found an issue; a nested portfolio is priced directly.
			addInstrument(key, entry) {
				if (quantityRef.current == null) {
					return base.addInstrument(key, entry);
				}
				const date = marketDate.value;
				const [row] = startChecks([emptyQuantityRow(entry)], date);
				const editor = base.addInstrument(key, row);
				checkOrPrice([row], date);
				return editor;
			},
			bulkAddInstruments(instruments) {
				if (quantityRef.current == null) {
					return base.bulkAddInstruments(instruments);
				}
				// Only rows that are genuinely new are priced: the base method drops an id the
				// composition already holds, and pricing that id would rewrite a row the user never added.
				const date = marketDate.value;
				const composition = base.latestComposition();
				const rows = startChecks(
					instruments.filter((instrument) => !composition.has(instrument.id)).map(emptyQuantityRow),
					date,
				);
				const editor = base.bulkAddInstruments(rows);
				checkOrPrice(rows, date);
				return editor;
			},
			setAum(rawAum) {
				const quantity = quantityRef.current;
				if (!quantity || !Number.isFinite(rawAum) || rawAum <= 0) {
					return;
				}
				quantityRef.current = { ...quantity, aum: rawAum };
				// Unlike a quantity/market-value edit, a budget change re-derives EVERY row's
				// weight, so it writes back in one bulk pass. The provider spreads the live
				// composition row, so `deleted` flags survive untouched.
				const result = applyAumEdit({ rows: engineRows(), aum: rawAum });
				const derivedById = new Map(
					result.rows.map((row) => [row.id, { weight: row.weight, marketValue: row.marketValue }]),
				);
				base.bulkEditInstruments(Set(derivedById.keys()), (row) => ({ ...row, ...derivedById.get(row.id) }));
			},
			// The cash row is structural in quantity mode — never deletable (its ACTION cell is
			// hidden, but group-row toggles and bulk selections can still address it by id), so
			// filter it out before delegating and rebalance cash with the freed market value.
			delete: (
				...args:
					| [mode: "soft", keys: string | Iterable<string>, toggle: boolean]
					| [mode: "hard", keys: string | Iterable<string>]
			) => {
				if (quantityRef.current == null) {
					if (args[0] === "hard") {
						return base.delete(...args);
					}
					return base.delete(...args);
				}
				const keys = typeof args[1] === "string" ? [args[1]] : args[1];
				const ids = Array.from(keys).filter((id) => !isCashRow(id));
				const afterDelete = args[0] === "hard" ? base.delete("hard", ids) : base.delete("soft", ids, args[2]);
				return recomputeCashWeight() ?? afterDelete;
			},
			reset(resetDataOrFn, opt) {
				base.reset((current) => {
					const init =
						typeof resetDataOrFn === "function"
							? resetDataOrFn({ ...current, quantity: quantityRef.current, lastTradeDate })
							: resetDataOrFn;
					const { quantity, lastTradeDate: nextLastTradeDate, ...baseInit } = normalizeQuantityInit(init);
					quantityRef.current = quantity;
					setLastTradeDate(nextLastTradeDate);
					return baseInit;
				}, opt);
			},
		};
	}, [base, marketDate, repricing, lastTradeDate, sizingQueue]);
}
